import { CommonModule } from '@angular/common';
import { Component, effect } from '@angular/core';
import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { HeavyHeaderComponent } from 'app/components/ui-kit/typography/heavy-header/heavy-header.component';
import { SecondaryBtnComponent } from "app/components/ui-kit/buttons/secondary-btn/secondary-btn.component";
import { SvgLoaderComponent } from "app/components/ui-kit/images/svg-loader/svg-loader.component";
import { SpinnerComponent } from "app/components/ui-kit/loaders/spinner/spinner.component";
import { AuthService } from 'app/services/auth.service';
import { Router } from '@angular/router';
import { AppStateService } from 'app/services/app-state.service';
import { StripeService } from 'app/services/stripe.service';
import { PopTrayComponent } from 'app/components/ui-kit/popovers/pop-tray/pop-tray.component';
import { holdPls } from 'app/utils/time/timing-utils';
import { SubscriptionEntitlement, SubscriptionInfo, SubscriptionService } from 'app/services/subscriptions.service';

@Component({
  selector: 'app-account',
  standalone: true,
  imports: [CommonModule, HeavyHeaderComponent, PageWrapperComponent, SecondaryBtnComponent, SvgLoaderComponent, SpinnerComponent, PopTrayComponent],
  templateUrl: './account.component.html',
  styleUrl: './account.component.scss'
})
export class AccountComponent {
  isDarkTheme = true;
  isLoading = true;
  isProcessing = false;
  deleteText = 'Delete Account';
  deleteWarningCount = 0;
  needsReauth = false;
  currentSub: SubscriptionInfo | null = null;
  subscriptionState: 'trial' | 'active' | 'inactive' = 'trial';
  renewalDate: Date | null = null;
  entitlementReason: SubscriptionEntitlement['reason'] | null = null;
  cancelActions: any;
  showCancelSub = false;
  cancellationMessage: string | null = null;

  showResumeSub: boolean = false;
  resumeActions: any;

  private resetTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private appState: AppStateService,
    private stripeService: StripeService,
    private subscriptionService: SubscriptionService
  ) {
    effect(() => {
      const profile = this.appState.user.profile();
      const subscription = this.appState.user.subscription();
      const entitlement = this.appState.user.entitlement();

      if (!profile?.id) {
        return;
      }

      this.currentSub = subscription as SubscriptionInfo | null;

      if (!entitlement || entitlement.userId !== profile.id) {
        this.isLoading = true;
        return;
      }
      this.applyEntitlement(entitlement);
    });
  }

  ngOnInit() {
    this.checkAuth();
    this.isDarkTheme = document.body.classList.contains('dark-theme');
    this.refreshSubscriptionState(true);

    this.cancelActions = {
      primaryButtons: [{ text: 'Cancel Subscription', function: () => this.handleCancelSub() }],
      secondaryButtons: [{ text: 'Close', function: () => this.onToggleCancelTray() }],
      closeFunction: () => this.onToggleCancelTray()
    };
    this.resumeActions = {
      primaryButtons: [{ text: 'Resume Subscription', function: () => this.handleResumeSub() }],
      secondaryButtons: [{ text: 'Close', function: () => this.onToggleResumeTray() }],
      closeFunction: () => this.onToggleResumeTray()
    };
  }

  async checkAuth() {
    this.needsReauth = await this.authService.needsReauth();
  }

  private applyEntitlement(entitlement: SubscriptionEntitlement) {
    this.subscriptionState = entitlement.status;
    this.renewalDate = entitlement.renewalDate;
    this.entitlementReason = entitlement.reason;
    this.cancellationMessage = this.buildCancellationMessage(this.currentSub);
    this.isLoading = false;
  }

  private buildCancellationMessage(subscription: SubscriptionInfo | null): string | null {
    if (!subscription?.isSubscribed) {
      return null;
    }

    const periodEnd = subscription.currentPeriodEnd;
    if (periodEnd instanceof Date) {
      return `Canceling will end access at the period end: ${periodEnd.toLocaleString()}.`;
    }

    return `Canceling will end access at the end of your current period.`;
  }

  onClickSubscribe() {
    this.router.navigate(['/subscribe']);
  }

  async onToggleCancelTray() {
    this.showCancelSub = !this.showCancelSub;
  }

  handleCancelSub() {
    this.showCancelSub = false;
    this.isLoading = true;
    this.isProcessing = true;
    this.stripeService.cancelSubscription().subscribe({
      next: () => { this.handleSubChange(); },
      error: (err) => {
        this.isProcessing = false;
        alert('err canceling');
        console.error(err);
      }
    });
  }

  async onToggleResumeTray() {
    this.showResumeSub = !this.showResumeSub;
  }

  handleResumeSub() {
    this.showResumeSub = false;
    this.isLoading = true;
    this.isProcessing = true;
    this.stripeService.resumeSubscription().subscribe({
      next: () => { this.handleSubChange(); },
      error: (err) => {
        this.isProcessing = false;
        alert('err resuming');
        console.error(err);
      }
    });
  }

  async handleSubChange() {
    await holdPls(4000);
    try {
      await this.refreshSubscriptionState(true);
    } finally {
      this.isProcessing = false;
    }
  }

  async onClickReauth() {
    ++this.deleteWarningCount;
    switch (this.deleteWarningCount) {
      case 1:
        this.deleteText = 'For security, you need to login again to delete. Click to Logout';
        this.coolDownDelete();
        break;
      case 2:
        this.deleteText = 'Logging Out....';
        await this.authService.signOut();
        window.location.reload();
        break;
      default:
        this.authService.signOut();
    }
  }

  async onClickDelete() {
    this.isProcessing = true;
    ++this.deleteWarningCount;
    switch (this.deleteWarningCount) {
      case 1:
        this.deleteText = 'You will lose access immediately. Click to Continue';
        this.coolDownDelete();
        break;
      case 2:
        this.deleteText = 'Last Chance. Confirm & Delete';
        this.coolDownDelete();
        break;
      case 3:
        try {
          await this.authService.deleteAccount();
          window.location.reload();
        } catch (err) {
          console.error(err); // TODO: friendly UI
        }
    }
  }

  private coolDownDelete() {
    this.startResetTimer();
    setTimeout(() => { this.isProcessing = false; }, 2000);
  }

  private startResetTimer() {
    if (this.resetTimeoutId) clearTimeout(this.resetTimeoutId);
    this.resetTimeoutId = setTimeout(() => this.resetState(), 7000);
  }

  private resetState() {
    this.deleteWarningCount = 0;
    this.deleteText = 'Delete Account';
    this.isProcessing = false;
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }

  private async refreshSubscriptionState(forceRefresh = false): Promise<void> {
    const profile = this.appState.user.profile();
    if (!profile?.id) {
      return;
    }

    this.isLoading = true;
    this.appState.user.entitlement.set(null);

    try {
      const subscription = await this.subscriptionService.getSubscriptionInfo(forceRefresh);
      this.appState.user.subscription.set(subscription);

      const entitlement = await this.subscriptionService.getEntitlementState(profile, {
        subscription,
        forceRefresh
      });
      this.appState.user.entitlement.set(entitlement);
    } catch (err) {
      console.error('Failed to refresh subscription state', err);
      this.isLoading = false;
    }
  }
}
